import { EventDispatcher } from '@ribajs/events'
import { handleize, waitForProp } from '@ribajs/utils'
import type { ShopifyProductVariant } from '@ribajs/shopify'
import { ShopifyService } from '@ribajs/shopify'
import { ShopifyCartService } from './shopify-cart.service.js'

// Types
import type { AnalyticsInstance, PageData } from 'analytics'
import type {
  ShopifyCartObject,
  AnalyticsConfig,
  AnalyticsGtmConfig,
  ShopifyCartLineItem,
  AnalyticsItem,
  AnalyticsTrackAddToCart,
  AnalyticsTrackBeginCheckout,
  AnalyticsTrackViewItem,
  AnalyticsTrackClearEcommerce,
  AnalyticsTrackPurchase,
  ShopifyProduct,
  LineItemTrackingProperties,
  AnalyticsTrackPixelAddContact,
} from '../types/index.js'
import type { State } from '@ribajs/history'

/**
 * A singleton Wrapper für [Analytics](https://github.com/DavidWells/analytics).
 * This class has the same API, so you can use `page`, `identify` and `track` in your app as this is documented in `Analytics`.
 */
export class AnalyticsService {
  /** Singleton instance */
  protected static instance: AnalyticsService | null = null

  protected static trackedTransactionIds: Array<number | string> = []

  /** Original `Analytics` instance */
  public analytics?: AnalyticsInstance
  /** Listen for cart events */
  protected cartEvents = ShopifyCartService.events
  /** Listen for client side routing events */
  protected routerEvents = new EventDispatcher('main')

  protected gtmPlugin?: { name: string }

  public gtag(...args: any[]) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(args)
  }

  /** Private because this class is a singleton and the user should not create any instance himself */
  protected constructor(
    protected readonly config: AnalyticsConfig,
    protected readonly gtmConfig: Partial<AnalyticsGtmConfig> = {},
  ) {
    // Set default core config
    this.config.app ||= 'seeberger-shop'
    this.config.plugins ||= []

    // Set default GTM Plugin config
    gtmConfig.containerId ||= 'GTM-WXWZMS4'
    gtmConfig.debug ||= this.config.debug
    gtmConfig.dataLayerName ||= 'dataLayer'
    gtmConfig.transportUrl ||= 'https://gtm.seeberger.de'
    gtmConfig.tagId ||= 'G-CRC972769Q'
    gtmConfig.customScriptSrc ||= 'https://gtm.seeberger.de/gtm.js'

    this.debug('config', config)
    this.debug('gtmConfig', gtmConfig)

    this.init()
      .then(() => {
        this.debug('ready')
      })
      .catch(e => {
        console.warn(
          '[AnalyticsService] Error on init the module, are you using an ad blocker?',
          e,
        )
      })
  }

  protected async init() {
    const { Analytics } = await import('analytics')
    const { default: googleTagManager } = await import(
      '@analytics/google-tag-manager'
    )
    // Always add the GTM Plugin
    this.gtmPlugin = googleTagManager(this.gtmConfig)
    this.config.plugins?.push(this.gtmPlugin)
    this.analytics = Analytics(this.config)

    // Enable the plugin by default
    await this.analytics.plugins.enable(this.gtmPlugin.name)

    // See https://developers.google.com/tag-platform/tag-manager/server-side/dependency-serving?hl=de&tag=gtag#step-2
    if (this.gtmConfig.transportUrl && this.gtmConfig.tagId) {
      this.gtag('config', this.gtmConfig.tagId, {
        transport_url: this.gtmConfig.transportUrl,
        first_party_collection: true,
      })
    }

    this.addEventListeners()
    if (!window.router_enable) {
      // Simulate router page view as long as we have no client side routing
      this.onPage(
        'main',
        {
          url: window.location.toString(),
        },
        null,
        null,
        '',
        {},
        true,
      )
    }
  }

  public async waitForReady() {
    this.debug('[AnalyticsService] waitForReady...')
    await waitForProp('analytics', this)
    this.debug('[AnalyticsService] waitForReady DONE')
    if (!this.analytics) {
      throw new Error('unreachable')
    }
    return true
  }

  public debug(...data: any[]) {
    if (!this.config.debug) return

    if (typeof data[0] === 'string') {
      data[0] = `[AnalyticsService] ${data[0]}`
    }
    console.debug(...data)
  }

  public get isDebug() {
    return this.config.debug || false
  }

  public setDebug(debug?: boolean) {
    return (this.config.debug = debug || false)
  }

  /**
   * Analytics library configuration, set's the singleton instance of this class.
   *
   * After the library is initialized with config, the core API is exposed & ready for use in the application.
   * @example
   * import Analytics from 'analytics'
   * import pluginABC from 'analytics-plugin-abc'
   * import pluginXYZ from 'analytics-plugin-xyz'
   *
   * // initialize analytics
   * const analytics = AnalyticsService.setInstance({
   *   app: 'my-awesome-app',
   *   plugins: [
   *     pluginABC,
   *     pluginXYZ
   *   ]
   * })
   * @param config - analytics core config
   * @returns Analytics Instance
   */
  public static setInstance(
    config: AnalyticsConfig = {},
    gtmConfig: Partial<AnalyticsGtmConfig> = {},
  ) {
    if (this.instance) {
      throw new Error('The instance was already set!')
    }

    this.instance = new AnalyticsService(config, gtmConfig)
    return this.instance
  }

  public static getInstance() {
    return this.instance
  }

  public static hasInstance() {
    return !!this.instance
  }

  /** Destroy the singleton instance of this class */
  public async destroy() {
    AnalyticsService.instance = null
    this.removeEventListeners()
    if (!this.gtmPlugin) {
      return
    }
    await this.analytics?.plugins.disable(this.gtmPlugin.name, () => {
      console.debug(this.gtmPlugin?.name + ' disabled')
    })
  }

  /** Clear the previous ecommerce object */
  public async trackClearEcommerce() {
    await this.waitForReady()

    const trackData: AnalyticsTrackClearEcommerce = {
      ecommerce: null,
    }

    this.debug('trackClearEcommerce')

    return this.analytics?.track('clear_ecommerce', trackData)
  }

  /** Track an product detail page view event */
  public async trackPdpView(
    product: ShopifyProduct,
    variant: ShopifyProductVariant,
    currency: string,
    quantity: number,
    trackingProperties: LineItemTrackingProperties,
  ) {
    await this.waitForReady()

    const trackItem = this.transformProduct(
      product,
      variant,
      currency,
      quantity,
      trackingProperties,
    )

    const trackData: AnalyticsTrackViewItem = {
      ecommerce: {
        items: [trackItem],
      },
    }

    this.debug('trackPdpView trackData', trackData)

    await this.trackClearEcommerce()
    return await this.analytics?.track('view_item', trackData)
  }

  /** Track an add to cart event */
  public async trackAddToCart(
    product: ShopifyProduct,
    variantId: number,
    quantity: number,
    trackingProperties: LineItemTrackingProperties,
    cart: ShopifyCartObject,
  ) {
    await this.waitForReady()

    const lineItem = cart.items.find(item => item.variant_id === variantId)
    if (!lineItem) return

    // const variant = ShopifyProductService.getVariant(product, variantId);

    const trackItem = this.transformCartListItem(
      lineItem,
      quantity,
      cart.currency,
      trackingProperties,
    )

    const trackData: AnalyticsTrackAddToCart = {
      ecommerce: {
        currency: cart.currency,
        items: [trackItem],
        value: trackItem.price,
      },
    }

    this.debug('trackAddToCart trackData', trackData)
    // this.debug("trackAddToCart lineItem", lineItem);
    // this.debug("trackAddToCart variant", variant);
    // this.debug("trackAddToCart properties", properties);
    this.debug('trackAddToCart cart', cart)

    await this.trackClearEcommerce()
    return await this.analytics?.track('add_to_cart', trackData)
  }

  /** Track an begin checkout event */
  public async trackBeginCheckout(cart: ShopifyCartObject) {
    await this.waitForReady()

    this.debug('trackBeginCheckout cart', cart)

    const trackData: AnalyticsTrackBeginCheckout = {
      ecommerce: {
        currency: cart.currency,
        items: [],
        value: Number(
          ShopifyService.formatMoneyWithDelimiters(
            cart.total_price,
            2,
            '',
            '.',
          ),
        ),
      },
    }

    for (const lineItem of cart.items) {
      const trackItem = this.transformCartListItem(
        lineItem,
        lineItem.quantity,
        cart.currency,
      )
      trackData.ecommerce.items.push(trackItem)
    }

    this.debug('trackAddToCart trackData', trackData)

    await this.trackClearEcommerce()
    return await this.analytics?.track('begin_checkout', trackData)
  }

  /**
   * Track an begin checkout event
   * @param fake Set this to true if you want to output only the data that would be tracked, but without really tracking it (useful for debugging on the status order page)
   * @returns
   */
  public async trackPurchase(fake?: boolean) {
    await this.waitForReady()

    const checkout = window.Shopify?.checkout

    if (!checkout) {
      console.warn('checkout object not defined!')
      return
    }

    const transaction_id = checkout.order_name || checkout.order_id

    if (AnalyticsService.trackedTransactionIds.includes(transaction_id)) {
      console.warn(
        `Purchase with transaction_id "${transaction_id}" already tracked!`,
      )
      return
    }

    this.debug(
      `trackPurchase${fake ? ' (fake)' : ''} checkout object`,
      checkout,
    )

    const trackData: AnalyticsTrackPurchase = {
      ecommerce: {
        currency: checkout.currency,
        items: [],
        value: Number(checkout.total_price),
        transaction_id,
        tax: Number(checkout.total_tax),
        shipping: Number(checkout.shipping_rate.price),
      },
    }
    if (checkout.discount?.code) {
      trackData.ecommerce.coupon = checkout.discount?.code
    }

    for (const lineItem of checkout.line_items) {
      const trackItem = this.transformCartListItem(
        lineItem,
        lineItem.quantity,
        checkout.currency,
      )
      trackData.ecommerce.items.push(trackItem)
    }

    this.debug(`trackPurchase${fake ? ' (fake)' : ''} trackData`, trackData)

    if (!fake) {
      await this.trackClearEcommerce()
      AnalyticsService.trackedTransactionIds.push(transaction_id)
      return await this.analytics?.track('purchase', trackData)
    }
  }

  // TODO: Not used yet
  public async trackPixelAddContact(trackData: AnalyticsTrackPixelAddContact) {
    await this.trackClearEcommerce()
    this.debug(`trackPixelAddContact trackData`, trackData)
    return await this.analytics?.track('add_contact', trackData)
  }

  /** Start listening for cart and routing events */
  protected addEventListeners() {
    this.cartEvents.on(
      'ShopifyCart:request:complete',
      this.onCartComplete,
      this,
    )
    this.cartEvents.on('ShopifyCart:request:changed', this.onCartChanged, this)
    this.cartEvents.on('ShopifyCart:add', this.onCartAdd, this)

    // This is triggered by the router module on a new page is ready over the client side routing
    this.routerEvents.on('newPageReady', this.onPage, this)
  }

  /** Stop listening for cart and routing events */
  protected removeEventListeners() {
    this.cartEvents.off(
      'ShopifyCart:request:complete',
      this.onCartComplete,
      this,
    )
    this.cartEvents.off('ShopifyCart:request:changed', this.onCartChanged, this)
    this.cartEvents.off('ShopifyCart:add', this.onCartAdd, this)

    this.routerEvents.off('newPageReady', this.onPage, this)
  }

  /** Called on a new page view */
  protected async onPage(
    viewId: string,
    currentStatus: State,
    prevStatus: State | null,
    container: HTMLElement | null,
    newPageRawHTML: string,
    dataset: any,
    isInit: boolean,
  ) {
    await this.waitForReady()
    const url = new URL(currentStatus.url, window.location.origin)
    const pageData: PageData = {
      url: url.href,
      path: url.pathname,
      hash: url.hash,
      search: url.search,
    }

    this.debug('onPage pageData', pageData)
    this.debug('onPage status', currentStatus, dataset, isInit)
    await this.analytics?.page(pageData)
  }

  /** Called after a cart request is complete */
  protected onCartComplete(/*cart: ShopifyCartObject*/) {
    // nothing yet
  }

  /** Called if the cart has changed */
  protected onCartChanged(/*cart: ShopifyCartObject*/) {
    // nothing yet
  }

  /** Called if a item was added to the cart */
  protected async onCartAdd(
    product: ShopifyProduct,
    variantId: number,
    quantity: number,
    trackingProperties: LineItemTrackingProperties,
    cart: ShopifyCartObject,
  ) {
    await this.trackAddToCart(
      product,
      variantId,
      quantity,
      trackingProperties,
      cart,
    )
  }

  /** Set track item properties by cart line item properties */
  protected setItemProperties(
    trackItem: AnalyticsItem,
    properties: LineItemTrackingProperties = {},
  ) {
    // if (properties._collection) {
    //   if (properties._parentCollection) {
    //     trackItem.item_category = properties._parentCollection
    //     trackItem.item_category2 = properties._collection
    //   } else {
    //     trackItem.item_category = properties._collection
    //   }
    // }

    if (properties._isRecommendation) {
      trackItem.item_list_id = 'passende_produkte'
      trackItem.item_list_name = 'passende produkte'
    }

    return trackItem
  }

  /** Transforms the shopify cart item to a track item */
  protected transformCartListItem(
    lineItem: ShopifyCartLineItem,
    quantity: number,
    currency: string,
    trackingProperties: LineItemTrackingProperties = {},
  ) {
    const properties = { ...lineItem.properties, ...trackingProperties }
    // ToDo(checkout): final_line_price + handle is not available in checkout object
    const trackItem: AnalyticsItem = {
      item_id: lineItem.sku || lineItem.variant_id,
      item_name: lineItem.title,
      currency,
      item_variant: handleize(lineItem.variant_title || ''),
      price: lineItem.final_line_price
        ? Number(
            ShopifyService.formatMoneyWithDelimiters(
              lineItem.final_line_price,
              2,
              '',
              '.',
            ),
          )
        : +lineItem.final_price,
      discount: this.getLineItemDiscount(lineItem),
      quantity,
    }
    if (trackItem.discount === 0) {
      delete trackItem.discount
    }

    this.setItemProperties(trackItem, properties)

    return trackItem
  }

  protected getLineItemDiscount(lineItem: ShopifyCartLineItem) {
    let discount = Number(
      ShopifyService.formatMoneyWithDelimiters(
        lineItem.line_level_total_discount || lineItem.total_discount || 0,
        2,
        '',
        '.',
      ),
    )
    if (!discount) {
      const discountAllocations = lineItem.discount_allocations || []
      for (const discountAllocation of discountAllocations) {
        discount += Number(discountAllocation.amount || 0)
      }
    }
    return discount
  }

  /** Transforms the shopify product object to a track item */
  protected transformProduct(
    product: ShopifyProduct,
    variant: ShopifyProductVariant,
    currency: string,
    quantity: number,
    trackingProperties: LineItemTrackingProperties = {},
  ) {
    const trackItem: AnalyticsItem = {
      item_id: variant.sku || variant.id,
      item_name: product.title,
      currency,
      item_variant: handleize(variant.title || ''),
      price: Number(
        ShopifyService.formatMoneyWithDelimiters(
          variant.price * quantity,
          2,
          '',
          '.',
        ),
      ),
      quantity,
    }

    this.setItemProperties(trackItem, trackingProperties)

    return trackItem
  }
}
